import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import am4lang_es_ES from '@amcharts/amcharts4/lang/es_ES';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '@environment/environment';
import { ColorService } from 'bp/shared/services/color.service';
import am4themes_betterplan from 'projects/bp-portal/src/bp/shared/chart/betterplan-theme-amchart';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
am4core.addLicense(environment.amchartLicence);
am4core.useTheme(am4themes_betterplan);
am4core.options.autoDispose = false;

@Component({
    selector: 'no-data-chart',
    templateUrl: './no-data-chart.component.html',
    styleUrls: ['./no-data-chart.component.scss'],
    standalone: true,
    imports: [
        MatButtonToggleModule,
        FormsModule,
        ReactiveFormsModule,
    ],
})
export class NoDataChartComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  private chart: am4charts.XYChart;

  @Input() chartSelectedOption: string;
  @Input() customMessage: string;
  @Input() canChangeTimePeriod = false;
  @Input() goalYears: number;
  @Input() wasTheGoalCreatedThePastYear: boolean;
  @Input() wasTheGoalCreatedThePastPastYear: boolean;
  @Input() thisYear: number;
  @Input() pastYear: number;
  @Input() pastPastYear: number;

  balanceColor = this.colorService.getBlue(0);
  depositColor = this.colorService.getGreen(0);
  balanceChartontrol = new FormControl('all');

  @ViewChild('chartgdsbalancesingle') chartgdsbalancesingle: ElementRef;

  @Output() chartSelectedOptionChangeEvent = new EventEmitter<string>();

  constructor(private zone: NgZone, private colorService: ColorService) {}

  ngOnDestroy() {
    this.zone.runOutsideAngular(() => {
      if (this.chart) {
        this.chart.dispose();
      }
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    this.ngAfterViewInit();

    if (changes.chartSelectedOption?.currentValue) {
      this.balanceChartontrol.setValue(this.chartSelectedOption);
    }
  }
  ngOnInit() {
    // suscribe to the balanceChartontrol to emit the time limits
    // that we want to see in the chart
    this.balanceChartontrol.valueChanges.subscribe({
      next: value => {
        this.chartSelectedOptionChangeEvent.emit(value ? value : 'all');
      },
    });
  }
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.zone.runOutsideAngular(() => {
        if (this.chart) {
          this.chart.dispose();
        }

        const chart = am4core.create(this.chartgdsbalancesingle.nativeElement, am4charts.XYChart);
        this.chart = chart;
        this.chart.tapToActivate = true;

        if (!this.chart) return;
        const categoryAxis = this.chart?.xAxes?.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'category';

        categoryAxis.renderer.minGridDistance = 40;
        categoryAxis.renderer.grid.template.disabled = true;
        categoryAxis.renderer.line.strokeOpacity = 1;
        categoryAxis.renderer.line.strokeWidth = 0.5;
        categoryAxis.tooltip!.disabled = true;

        const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.tooltip!.disabled = true;
        valueAxis.renderer.grid.template.disabled = true;
        valueAxis.renderer.opposite = true;
        valueAxis.renderer.line.strokeOpacity = 1;
        valueAxis.renderer.line.strokeWidth = 0.5;
        valueAxis.min = 0;
        valueAxis.numberFormatter.numberFormat = '';
        valueAxis.numberFormatter.bigNumberPrefixes = [
          { number: 1e3, suffix: 'k' },
          { number: 1e6, suffix: 'M' },
        ];

        this.chart.cursor = new am4charts.XYCursor();
        this.chart.cursor.xAxis = categoryAxis;
        this.chart.cursor.lineY.disabled = true;
        this.chart.language.locale = am4lang_es_ES;

        const columnSeries = this.chart.series.push(new am4charts.ColumnSeries());
        columnSeries.dataFields.categoryX = 'category';
        columnSeries.dataFields.valueY = 'value';
        columnSeries.dataFields.dateX = 'date';
        columnSeries.fillOpacity = 0.3;
        columnSeries.columns.template.adapter.add('fill', (fill, target) => {
          let data = target.tooltipDataItem.dataContext as any;
          return data.category == 'Balance'
            ? am4core.color(this.balanceColor)
            : am4core.color(this.depositColor);
        });

        if (this.chart.series.length > 0) {
          this.chart.series.values[0].tooltip!.getFillFromObject = false;
          this.chart.series.values[0].tooltip!.background.fill = am4core.color('#FFFFFF');
          this.chart.series.values[0].tooltip!.autoTextColor = false;
          this.chart.series.values[0].tooltip!.label.fill = am4core.color('black');
        }

        valueAxis.disabled = true;
        categoryAxis.disabled = true;
        this.chart.cursor.disabled = true;

        this.detectEmptyChart();
      });
    }, 200);
  }

  detectEmptyChart(): void {
    const emptyLabel = this.chart.createChild(am4core.Label);

    if (this.customMessage) {
      emptyLabel.text = `[bold font-size:15x]` + this.customMessage;
    }

    emptyLabel.y = am4core.percent(25);
    emptyLabel.x = am4core.percent(50);
    emptyLabel.y = am4core.percent(50);
    emptyLabel.align = 'center';
    emptyLabel.horizontalCenter = 'middle';
    emptyLabel.verticalCenter = 'middle';
    emptyLabel.isMeasured = false;
    emptyLabel.wrap = true;
    emptyLabel.textAlign = 'middle';
    emptyLabel.show();

    if (!this.customMessage) {
      this.chart.xAxes.clear();
      this.chart.yAxes.clear();
      this.chart.paddingTop = 0;
      this.chart.paddingBottom = 0;
      this.chart.paddingLeft = 0;
      this.chart.paddingRight = 0;
      const dateAxis = this.chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.renderer.grid.template.disabled = true;
      dateAxis.renderer.line.disabled = true;
      dateAxis.renderer.labels.template.opacity = 0;
      dateAxis.renderer.grid.template.location = 0;
      dateAxis.startLocation = 0.5;
      dateAxis.endLocation = 0.5;

      dateAxis.startIndex = 0;

      dateAxis.renderer.cellStartLocation = 0;
      const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.grid.template.disabled = true;
      valueAxis.cursorTooltipEnabled = false;
      valueAxis.renderer.opposite = true;
      valueAxis.renderer.line.strokeOpacity = 1;
      valueAxis.renderer.line.strokeWidth = 0.5;
      valueAxis.renderer.padding(0, 0, 0, 0);
      valueAxis.renderer.margin(0, 0, 0, 0);
      valueAxis.renderer.labels.template.disabled = true;

      valueAxis.renderer.line.disabled = true;
      valueAxis.renderer.baseGrid.disabled = true;

      this.chart.data = [];
      this.chart.series.clear();
      const lineSeries = this.chart.series.push(new am4charts.LineSeries());
      lineSeries.dataFields.dateX = 'date';
      lineSeries.dataFields.valueY = 'deposit';
      lineSeries.name = 'deposit';
      lineSeries.tensionX = 0.77;
      lineSeries.tensionY = 0.77;

      lineSeries.fillOpacity = 0.7;
      let fillModifier = new am4core.LinearGradientModifier();
      fillModifier.opacities = [1, 0];
      fillModifier.offsets = [0, 1];
      fillModifier.gradient.rotation = 90;
      lineSeries.segments.template.fillModifier = fillModifier;
      lineSeries.fill = am4core.color(this.balanceColor);
      lineSeries.stroke = am4core.color(this.balanceColor);
      lineSeries.strokeOpacity = 1;
      lineSeries.strokeWidth = 1.2;
      let array = [
        { deposit: 20, date: '2020-08-09T04:00:00.000Z' },
        { deposit: 40, date: '2020-08-11T04:00:00.000Z' },
        { deposit: 30, date: '2020-08-14T04:00:00.000Z' },
        { deposit: 60, date: '2020-08-17T04:00:00.000Z' },
        { deposit: 50, date: '2020-08-21T04:00:00.000Z' },
        { deposit: 90, date: '2020-08-24T04:00:00.000Z' },
      ];
      this.chart.data = array;
    }
  }
}
